import React, { useCallback, useContext, useEffect } from 'react';
import {
  Form,
  Modal,
  Row,
  InputNumber,
  Col,
  Switch,
  Divider,
  Card,
  Tabs,
  Button,
  notification
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import TextArea from 'antd/lib/input/TextArea';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { shouldUpdateCompare } from 'utils/utils2';
import FormInput from 'components/FormInput';
import { toCurrency, toRate } from '../../../utils/utils';
import ThirdPartyInput from 'components/ThirdPartyInput';
import ParListSelect from 'components/ParListSelect';
import Value from 'components/Value';
import TaxSelect from 'components/TaxSelect';
import GetLabel from 'DisplayFramework/components/GetLabel';
import InputCurrency from 'components/InputCurrency';
import { onFinishFailed } from 'components/DisplayValidationErrors/DisplayValidationErrors';
import { AuthContext } from 'components/Auth/Auth';
import moment from 'moment';
import AssetFeesPictures from './AssetFeesPictures';
import { addAssetFees, updateAssetFees } from 'services/assets';

export type AssetFessModalProps = {
  addorupdate: 'ADD' | 'UPDATE';
  visible: boolean;
  iscreate?: boolean;
  onCancel: () => void;
  currcode: string;
  assetId: number;
  record?: any;
  initialValues: any;
  refetch: () => void;
};
const { TabPane } = Tabs;

const AssetFessModal = (props: AssetFessModalProps) => {
  const {
    visible,
    onCancel,
    addorupdate,
    currcode,
    record,
    assetId,
    initialValues,
    refetch
  } = props;

  const [form] = useForm();
  const { isInspect } = useContext(AuthContext);

  const recalculate = useCallback(
    (updatedValues: any) => {
      let feesoriginalnetamount = form.getFieldValue('feesoriginalnetamount');
      let feesdiscountrate = form.getFieldValue('feesdiscountrate');
      let taxrate =
        updatedValues?.taxrate !== undefined
          ? updatedValues?.taxrate
          : form.getFieldValue('taxrate');
      let feesnetamount =
        feesoriginalnetamount -
        feesoriginalnetamount * (feesdiscountrate / 100);
      let feestaxamount = feesnetamount * (taxrate / 100);
      let fessgrossamount = feesnetamount + feestaxamount;
      form.setFieldsValue({
        taxrate: taxrate,
        feesoriginalnetamount: feesoriginalnetamount,
        feesdiscountrate: feesdiscountrate,
        feesnetamount: feesnetamount,
        feestaxamount: feestaxamount,
        fessgrossamount: fessgrossamount
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form]
  );
  useEffect(() => {
    form.setFieldsValue({
      ...record,
      status:
        addorupdate === 'ADD'
          ? `initial (${moment().format('YYYY-MM-DD')})`
          : record?.feesstatuslabel +
            ' (' +
            record?.feesstatusdate.substring(0, 10) +
            ')',
      feesstatus: addorupdate === 'ADD' && 'INIT',
      supplier: {
        tpid: record?.tpidsupplier,
        tpname: record?.suppliername,
        tpreference: record?.supplierreference
      },
      picture1after: record?.picture1after,
      picture2after: record?.picture2after,
      picture3after: record?.picture3after,
      picture1before: record?.picture1before,
      picture2before: record?.picture2before,
      picture3before: record?.picture3before
    });
  }, [addorupdate, record]);

  const onSave = useCallback(
    (values: any) => {
      const currentValue = {
        ...values,
        assetid: assetId,
        tpidsupplier: form.getFieldValue('supplier').tpid,
        suppliername: form.getFieldValue('supplier').tpname,
        supplierreference: form.getFieldValue('supplier').tpreference,
        picture1after: form.getFieldValue('picture1after'),
        picture2after: form.getFieldValue('picture2after'),
        picture3after: form.getFieldValue('picture3after'),
        picture1before: form.getFieldValue('picture1before'),
        picture2before: form.getFieldValue('picture2before'),
        picture3before: form.getFieldValue('picture3before')
      };
      addorupdate === 'ADD'
        ? addAssetFees({
            request: { ...currentValue, status: 'INIT' }
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message
              });
              refetch();
            }
          })
        : updateAssetFees({
            request: { ...currentValue },
            feesid: record.feesid
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message
              });
              refetch();
            }
          });
    },
    [form, record]
  );

  return (
    <Modal
      visible={visible}
      title={
        addorupdate === 'ADD' ? (
          <GetLabel Inspectstyle={{ width: '30%' }} label="AddAssetFees" />
        ) : (
          <GetLabel Inspectstyle={{ width: '30%' }} label="updateAssetFees" />
        )
      }
      okText="Add"
      onCancel={() => {
        onCancel();
      }}
      footer={[
        <Button key="cancel" onClick={() => !isInspect && onCancel()}>
          <GetLabel label="cancel" />
        </Button>,
        <Button
          key="add"
          type="primary"
          onClick={form.submit}
          disabled={record?.feesstatus === 'CANCEL'}
        >
          <GetLabel label="add" />
        </Button>
      ]}
      width="1400px"
      style={{ top: 20 }}
    >
      <Form
        form={form}
        onFinish={(e) => {
          onSave(e);
        }}
        initialValues={initialValues}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
      >
        <Card>
          <Tabs animated defaultActiveKey="1" type="card">
            <TabPane tab={<GetLabel label="AssetFeesDetails" />} key="1">
              <Row gutter={16}>
                <Col span={16}>
                  <Row gutter={8}>
                    <FormInput
                      rules={[{ required: true }]}
                      comcode="feestype"
                      name="feestype"
                      col
                      xs={6}
                      shouldUpdate
                    >
                      <ParListSelect parcode="ASSFEESTYP" />
                    </FormInput>
                    <Form.Item
                      rules={[{ required: true }]}
                      shouldUpdate={shouldUpdateCompare('feestype')}
                      noStyle
                    >
                      {({ getFieldValue }) => (
                        <FormInput
                          comcode="feescategory"
                          col
                          xs={6}
                          name="feescategory"
                          rules={[{ required: true }]}
                        >
                          <ParListSelect
                            parcode="ASSFEESCAT"
                            parvalueparent={getFieldValue('feestype')}
                          />
                        </FormInput>
                      )}
                    </Form.Item>
                    <Col xs={6} />
                    <Form.Item
                      rules={[{ required: true }]}
                      shouldUpdate={shouldUpdateCompare('feestype')}
                      noStyle
                    >
                      {({ getFieldValue }) => (
                        <FormInput
                          comcode="status"
                          col
                          xs={6}
                          name="status"
                          rules={[{ required: true }]}
                        >
                          <Value />
                        </FormInput>
                      )}
                    </Form.Item>
                  </Row>
                  <Row>
                    <FormInput
                      col
                      xs={6}
                      comcode="supplier"
                      rules={[{ required: true }]}
                      name="supplier"
                    >
                      <ThirdPartyInput
                        role="SUPPLIER"
                      />
                    </FormInput>

                    <FormInput
                      col
                      xs={24}
                      name="feesdescription"
                      comcode="feesdescription"
                      rules={[{ required: true }]}
                    >
                      <TextArea rows={7} />
                    </FormInput>
                  </Row>
                </Col>
                <Col span={8}>
                  <Card type="inner" style={{ background: '#F6F3F2' }}>
                    <Row gutter={16}>
                      <InputCurrency
                        col
                        md={12}
                        onChange={(value) =>
                          recalculate({ comporiginalnetamount: value })
                        }
                        currcode={currcode}
                        fieldname="feesoriginalnetamount"
                        comcode="feesoriginalnetamount"
                        rules={[{ required: true }]}
                      />
                      <FormInput
                        col
                        xs={12}
                        name="feesdiscountrate"
                        comcode="discount"
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          formatter={(value) => `${value}%`}
                          onChange={(value) =>
                            recalculate({ discountRate: value })
                          }
                        />
                      </FormInput>

                      <FormInput
                        col
                        xs={12}
                        name="feesnetamount"
                        comcode="netamount"
                      >
                        <Value
                          formatter={(value) => toCurrency(value, currcode)}
                        />
                      </FormInput>
                      <FormInput
                        name="taxcode"
                        col
                        xs={12}
                        shouldUpdate
                        comcode="taxcode"
                        rules={[{ required: true }]}
                      >
                        <TaxSelect
                          taxflow="INOUT"
                          onSelected={(tax) =>
                            recalculate({ taxrate: tax.taxrate })
                          }
                        />
                      </FormInput>

                      <FormInput
                        col
                        xs={24}
                        md={12}
                        name="feestaxamount"
                        comcode="taxamount"
                      >
                        <Value
                          formatter={(value) => toCurrency(value, currcode)}
                        />
                      </FormInput>
                      <FormInput
                        col
                        xs={24}
                        md={12}
                        name="fessgrossamount"
                        comcode="grossamount"
                      >
                        <Value
                          formatter={(value) => toCurrency(value, currcode)}
                        />
                      </FormInput>
                      <FormInput
                        col
                        xs={12}
                        name="taxrate"
                        comcode="taxrate"
                        hidden
                      >
                        <Value formatter={toRate} />
                      </FormInput>
                    </Row>
                  </Card>
                </Col>
              </Row>
              <Divider></Divider>
              <Row gutter={16}>
                <FormInput
                  col
                  xs={3}
                  name="flagrebilling"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                  />
                </FormInput>
              </Row>
            </TabPane>
            <TabPane tab={<GetLabel label="AssetFeesPicture" />} key="2">
              <AssetFeesPictures form={form} />
            </TabPane>
          </Tabs>
        </Card>
      </Form>
    </Modal>
  );
};

export default AssetFessModal;
